import React, { useState, useEffect, useCallback } from "react";
import { COLORS } from "../../../../theme/colors";
import { MATERIALS } from "../../../../theme/materials";
import { agentApi, authApi } from "../../../../api/client";
import TabBtn from "../../../../components/TabBtn";

// agent-experience-redesign-v1.md §6.3 (F9) — dual-mode channel
// binding modal. Two tabs:
//   tab (a) "Connect existing" — pick from tenant channels that
//           are already configured (credentials present); click
//           Bind → A3 bind_channel route writes to agents.channels.
//   tab (b) "Create new" — pick a channel type, fill the per-
//           channel credential form, save via the existing
//           per-channel update endpoint, then auto-bind.
//
// CRITICAL: per Q8 + §6.3, "create new for this agent" does NOT
// create per-agent credential isolation — credentials remain
// tenant-scoped per the existing tenants.settings_json invariant.
// The drawer header explains this so developers don't mistakenly
// assume isolation.
//
// channelType ∈ {whatsapp, sms, voice, chat, email, webhook}.
// Voice + chat have no per-channel credential form (voice
// configures via Genesys / LiveKit at platform level; chat is
// always available); they show as connect-only.

const CHANNELS = [
  {
    type: "whatsapp", label: "WhatsApp",
    description: "WhatsApp Cloud API",
    fields: [
      { name: "phone_number_id", label: "Phone number ID", required: true },
      { name: "access_token",    label: "Access token",    required: true, secret: true },
      { name: "verify_token",    label: "Verify token",    required: true, secret: true,
        hint: "Used to verify Meta webhook callbacks" },
      { name: "app_secret",      label: "App secret",      required: false, secret: true },
    ],
    getFn: () => authApi.getWhatsAppConfig(),
    saveFn: (data) => authApi.updateWhatsAppConfig(data),
    testFn: () => authApi.testWhatsAppConfig(),
  },
  {
    type: "sms", label: "SMS (Twilio)",
    description: "Twilio SMS",
    fields: [
      { name: "account_sid",   label: "Account SID",   required: true },
      { name: "auth_token",    label: "Auth token",    required: true, secret: true },
      { name: "phone_number",  label: "Phone number",  required: true,
        hint: "E.164 format, e.g. +14155550100" },
    ],
    getFn: () => authApi.getSMSConfig(),
    saveFn: (data) => authApi.updateSMSConfig(data),
    testFn: () => authApi.testSMSConfig(),
  },
  {
    type: "email", label: "Email (SMTP)",
    description: "SMTP outbound — credentials per CLAUDE.md tenant settings",
    fields: [
      { name: "host",       label: "SMTP host",     required: true,
        hint: "smtp.example.com" },
      { name: "port",       label: "Port",          required: true, number: true,
        defaultValue: 587 },
      { name: "username",   label: "Username",      required: true },
      { name: "password",   label: "Password",      required: true, secret: true },
      { name: "from_email", label: "From email",    required: true },
      { name: "use_tls",    label: "Use TLS",       boolean: true, defaultValue: true },
    ],
    getFn: () => authApi.getSmtpConfig(),
    saveFn: (data) => authApi.updateSmtpConfig(data),
    testFn: () => authApi.testSmtpConfig(),
  },
  {
    type: "voice", label: "Voice",
    description: "Voice channel — configured at platform level via LiveKit + Twilio SIP. No per-channel credential form here.",
    fields: [],
    connectOnly: true,
  },
  {
    type: "chat", label: "Webchat",
    description: "Webchat widget — always available; no credentials needed.",
    fields: [],
    connectOnly: true,
  },
  {
    type: "webhook", label: "Webhook",
    description: "Inbound webhook — incoming HTTP POSTs to /webhooks/{workspace}/<provider> are routed to this agent. Configure per-webhook destinations in the Webhooks tab; binding here flips the agent's listener flag.",
    fields: [],
    connectOnly: true,
  },
];

export default function BindChannelModal({ agentId, agentChannels = [], onClose, onBound }) {
  const [tab, setTab] = useState("existing");
  const [type, setType] = useState("whatsapp");
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [configured, setConfigured] = useState({}); // {channelType: bool}
  const [loading, setLoading] = useState(true);
  // Form state for create-new tab
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const cfg = CHANNELS.find(c => c.type === type) || CHANNELS[0];

  // On mount: probe each channel's getFn to figure out which are
  // already configured (credentials present). Failed probes are
  // treated as "not configured."
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all(CHANNELS.map(async (c) => {
      if (c.connectOnly) return [c.type, true];
      try {
        const res = await c.getFn();
        const data = res?.data || res || {};
        // Heuristic: at least one required field has a stored value.
        const required = c.fields.filter(f => f.required);
        const ok = required.some(f => data[f.name] != null && data[f.name] !== "");
        return [c.type, ok];
      } catch {
        return [c.type, false];
      }
    })).then((rows) => {
      if (cancelled) return;
      const map = {};
      for (const [k, v] of rows) map[k] = v;
      setConfigured(map);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Reset form when switching channel type (defaults from spec).
  useEffect(() => {
    const init = {};
    for (const f of (cfg.fields || [])) {
      if (f.defaultValue !== undefined) init[f.name] = f.defaultValue;
      else if (f.boolean) init[f.name] = false;
      else init[f.name] = "";
    }
    setForm(init);
    setTestResult(null);
    setError(null);
  }, [type, cfg]);

  const bindNow = useCallback(async (channelType) => {
    setBusy(channelType);
    setError(null);
    try {
      await agentApi.bindChannel(agentId, channelType);
      onBound && onBound(channelType);
      onClose && onClose();
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setBusy(null);
    }
  }, [agentId, onBound, onClose]);

  const submitCreate = useCallback(async (e) => {
    e?.preventDefault?.();
    setError(null);
    if (cfg.connectOnly) {
      // Just bind — no credentials to save.
      return bindNow(type);
    }
    for (const f of cfg.fields) {
      if (f.required && !String(form[f.name] || "").trim()) {
        setError(`${f.label} is required`);
        return;
      }
    }
    setSubmitting(true);
    try {
      // Strip empty / unchanged fields where possible.
      const payload = {};
      for (const [k, v] of Object.entries(form)) {
        if (v === "" || v === null || v === undefined) continue;
        payload[k] = v;
      }
      await cfg.saveFn(payload);
      // Auto-bind this agent to the freshly-configured channel.
      await agentApi.bindChannel(agentId, type);
      onBound && onBound(type);
      onClose && onClose();
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setSubmitting(false);
    }
  }, [cfg, form, type, agentId, bindNow, onBound, onClose]);

  const test = useCallback(async () => {
    if (!cfg.testFn) return;
    setError(null);
    setTesting(true);
    setTestResult(null);
    try {
      const res = await cfg.testFn();
      setTestResult(res?.data || res || { status: "ok" });
    } catch (e) {
      setTestResult({ status: "error", error: e?.message || String(e) });
    } finally {
      setTesting(false);
    }
  }, [cfg]);

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: COLORS.surface, border: `1px solid ${COLORS.border}`,
        borderRadius: 12, width: "90%", maxWidth: 540, maxHeight: "90vh",
        overflow: "hidden", display: "flex", flexDirection: "column",
        boxShadow: MATERIALS?.spatialShadow ? MATERIALS.spatialShadow() : "0 16px 40px rgba(0,0,0,0.5)",
      }}>
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>Bind channel</div>
            <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 4, lineHeight: 1.4 }}>
              Channel credentials remain <strong>tenant-scoped</strong>;
              binding here only attaches this agent to the channel's routing.
            </div>
          </div>
          <button type="button" onClick={onClose} style={{
            background: "transparent", border: "none", color: COLORS.textDim, cursor: "pointer", fontSize: 16, padding: 4,
          }}>✕</button>
        </div>

        <div style={{ display: "flex", borderBottom: `1px solid ${COLORS.border}`, background: COLORS.surface }}>
          <TabBtn active={tab === "existing"} onClick={() => setTab("existing")} label="Connect existing" />
          <TabBtn active={tab === "create"} onClick={() => setTab("create")} label="Create new" />
        </div>

        <div style={{ flex: 1, overflow: "auto", padding: 20 }}>
          {error && (
            <div style={{
              padding: "8px 12px", borderRadius: 6, marginBottom: 12,
              background: `${COLORS.pink || "#ef4444"}15`,
              border: `1px solid ${COLORS.pink || "#ef4444"}55`,
              color: COLORS.pink || "#ef4444", fontSize: 12,
            }}>{error}</div>
          )}

          {tab === "existing" && (
            <>
              {loading ? (
                <div style={{ color: COLORS.textDim, fontSize: 12 }}>Probing channel configurations…</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {CHANNELS.map(c => {
                    const isConfigured = configured[c.type];
                    const isBound = agentChannels.includes(c.type);
                    return (
                      <div key={c.type} style={{
                        padding: 12, borderRadius: 8,
                        border: `1px solid ${isBound ? COLORS.green + "44" : COLORS.border}`,
                        background: COLORS.surface,
                        display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12,
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{c.label}</div>
                          <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 2 }}>{c.description}</div>
                          {!isConfigured && (
                            <div style={{ fontSize: 10, color: COLORS.orange, marginTop: 4 }}>
                              Not configured — switch to <strong>Create new</strong> to set up.
                            </div>
                          )}
                        </div>
                        <button type="button"
                          disabled={busy === c.type || !isConfigured || isBound}
                          onClick={() => bindNow(c.type)}
                          style={{
                            padding: "5px 14px", borderRadius: 6, border: "none",
                            background: isBound ? `${COLORS.green}25` : COLORS.accent,
                            color: isBound ? COLORS.green : "#fff",
                            fontSize: 11, fontWeight: 600,
                            cursor: (busy === c.type || !isConfigured || isBound) ? "default" : "pointer",
                            opacity: (busy === c.type) ? 0.6 : (!isConfigured || isBound ? 0.45 : 1),
                            whiteSpace: "nowrap",
                          }}>
                          {busy === c.type ? "Binding…" : isBound ? "● Bound" : "Bind"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {tab === "create" && (
            <form onSubmit={submitCreate} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div>
                <label style={labelStyle}>Channel type</label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  style={fieldStyle}
                >
                  {CHANNELS.map(c => (
                    <option key={c.type} value={c.type}>{c.label}</option>
                  ))}
                </select>
                <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 3 }}>{cfg.description}</div>
              </div>

              {cfg.fields.map(f => (
                <div key={f.name}>
                  <label style={labelStyle}>
                    {f.label}{f.required && <span style={{ color: COLORS.pink || "#ef4444" }}> *</span>}
                  </label>
                  {f.boolean ? (
                    <label style={{
                      display: "flex", alignItems: "center", gap: 8,
                      padding: "6px 10px", borderRadius: 6,
                      border: `1px solid ${COLORS.border}`, cursor: "pointer",
                    }}>
                      <input type="checkbox" checked={!!form[f.name]}
                        onChange={(e) => setForm(p => ({ ...p, [f.name]: e.target.checked }))} />
                      <span style={{ fontSize: 11, color: COLORS.text }}>{form[f.name] ? "On" : "Off"}</span>
                    </label>
                  ) : (
                    <input
                      type={f.secret ? "password" : (f.number ? "number" : "text")}
                      value={form[f.name] ?? ""}
                      onChange={(e) => setForm(p => ({
                        ...p,
                        [f.name]: f.number ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value,
                      }))}
                      placeholder={f.placeholder}
                      style={fieldStyle}
                    />
                  )}
                  {f.hint && (
                    <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 3 }}>{f.hint}</div>
                  )}
                </div>
              ))}

              {testResult && (
                <div style={{
                  padding: "8px 12px", borderRadius: 6,
                  background: (testResult.status === "ok" || testResult.success === true)
                    ? `${COLORS.green}15` : `${COLORS.pink || "#ef4444"}15`,
                  border: `1px solid ${(testResult.status === "ok" || testResult.success === true)
                    ? COLORS.green : COLORS.pink || "#ef4444"}55`,
                  color: (testResult.status === "ok" || testResult.success === true)
                    ? COLORS.green : COLORS.pink || "#ef4444",
                  fontSize: 11, lineHeight: 1.5,
                }}>
                  <strong>{(testResult.status === "ok" || testResult.success === true) ? "✓ Connected" : "✕ Failed"}</strong>
                  {testResult.error && <span> — {testResult.error}</span>}
                </div>
              )}

              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
                <button type="button" onClick={onClose} style={{
                  background: "transparent", border: `1px solid ${COLORS.border}`,
                  color: COLORS.textDim, padding: "6px 14px", borderRadius: 6,
                  fontSize: 12, cursor: "pointer",
                }}>Cancel</button>
                {!cfg.connectOnly && cfg.testFn && (
                  <button type="button" onClick={test} disabled={testing} style={{
                    background: "transparent", border: `1px solid ${COLORS.cyan}66`,
                    color: COLORS.cyan, padding: "6px 14px", borderRadius: 6,
                    fontSize: 12, fontWeight: 600, cursor: testing ? "default" : "pointer",
                    opacity: testing ? 0.6 : 1,
                  }}>{testing ? "Testing…" : "Test connection"}</button>
                )}
                <button type="submit" disabled={submitting} style={{
                  background: COLORS.accent, color: "#fff", border: "none",
                  padding: "6px 14px", borderRadius: 6,
                  fontSize: 12, fontWeight: 600, cursor: submitting ? "default" : "pointer",
                  opacity: submitting ? 0.6 : 1,
                }}>
                  {submitting ? "Saving…" : (cfg.connectOnly ? "Bind" : "Save + Bind")}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

const labelStyle = {
  display: "block", fontSize: 11, fontWeight: 600,
  color: COLORS.textMuted, marginBottom: 4, letterSpacing: "0.02em",
};

const fieldStyle = {
  width: "100%", boxSizing: "border-box",
  padding: "8px 12px", fontSize: 12, color: COLORS.text,
  background: COLORS.surface,
  border: `1px solid ${COLORS.border}`, borderRadius: 6,
  outline: "none", fontFamily: "inherit",
};
